import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { aboutValues } from '../mock';

const CompanyPage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="Company"
        description="About BriefingIQ — the team behind the enterprise platform for executive briefings and corporate events. Our mission, values, and story."
        path="/company"
        keywords={['about BriefingIQ', 'company', 'team', 'mission']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Company', path: '/company' },
        ]}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">Company</div>
          <h1 className="headline-xl text-ink mb-6">
            Built by industry professionals,<br />
            <span className="text-ink-3">for industry professionals.</span>
          </h1>
          <p className="text-lg md:text-xl text-ink-2 max-w-3xl leading-relaxed">
            We founded BriefingIQ to bring modern technology and AI-powered analytics to enterprise briefing and event management — shaped by teams who have run these programs at global scale.
          </p>
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-6">
          {aboutValues.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="bg-white rounded-2xl p-8 border border-[var(--line-2)]">
                <Icon className="w-6 h-6 text-ink mb-6" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-ink mb-3">{v.title}</h3>
                <p className="text-sm text-ink-2 leading-relaxed">{v.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Our story</div>
          <h2 className="headline-lg text-ink mb-8">A better way to engage.</h2>
          <div className="prose">
            <p>
              Our team brings decades of combined experience in executive briefing program management, corporate event operations, enterprise SaaS, and artificial intelligence. We&apos;ve walked in your shoes — coordinating executive customer visits, managing complex event logistics, and proving ROI to leadership.
            </p>
            <p>
              That&apos;s why BriefingIQ and EventIQ are designed to integrate seamlessly into your existing enterprise workflows — native Salesforce CRM and Microsoft Outlook, Zoom and Teams for hybrid events, and AI analytics that surface insights automatically.
            </p>
            <p>
              We&apos;re not just building software. We&apos;re building the future of how enterprises engage their most important stakeholders.
            </p>
          </div>
        </div>
      </section>

      <section id="careers" className="bg-[var(--bg-alt)] section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Careers</div>
          <h2 className="headline-lg text-ink mb-6">Join us.</h2>
          <p className="text-lg text-ink-2 leading-relaxed mb-8">
            We&apos;re a small, focused team building software that respects the people who use it. We hire senior operators, engineers, and designers who want to shape a category from the inside.
          </p>
          <Link to="/contact" className="btn-primary">
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="bg-[#0F172A] dark section relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="headline-lg text-white mb-6">See the platform.</h2>
          <button onClick={onOpenDemo} className="btn-invert">
            Request a demo <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </>
  );
};

export default CompanyPage;
